/**
 * cart-bar-picker
 *
 * Forwards a variant selection made inside the sticky <cart-bar> to the
 * main <variant-selects>, so the theme's existing product-info fetch
 * + swap pipeline updates price, image, availability, and the cart-bar
 * itself. Keeps the viewport pinned so the page doesn't jump to the
 * main picker after the swap.
 */
(() => {
  'use strict';

  /**
   * Freeze scroll behavior around a variant swap triggered from the cart-bar.
   *
   * Disables product-info's `variantImageScroll` (which would scroll the
   * newly-selected media into view) and restores window.scrollY after the
   * swap — product-info calls `.focus()` on the main radio post-swap, which
   * scrolls the offscreen top-of-page element into view otherwise.
   *
   * @param {HTMLElement} productInfo - the <product-info> element wrapping the PDP.
   */
  function suppressScroll(productInfo) {
    if (!productInfo) return;

    const savedScrollY = window.scrollY;
    const originalVariantImageScroll = productInfo.variantImageScroll;
    productInfo.variantImageScroll = false;

    let restored = false;
    let unsubscribeVariantChange;

    function restoreVariantImageScroll() {
      if (restored) return;
      restored = true;
      productInfo.variantImageScroll = originalVariantImageScroll;
      if (typeof unsubscribeVariantChange === 'function') unsubscribeVariantChange();
    }

    function restoreScrollPosition() {
      window.scrollTo(0, savedScrollY);
    }

    try {
      if (window.subscribe && window.theme?.PUB_SUB_EVENTS) {
        unsubscribeVariantChange = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, () => {
          restoreScrollPosition();
          requestAnimationFrame(() => {
            restoreScrollPosition();
            requestAnimationFrame(restoreScrollPosition);
          });
          restoreVariantImageScroll();
        });
      }
    } catch (error) {
      // fall through to timeout safety net
    }
    setTimeout(restoreVariantImageScroll, 3000);
  }

  /**
   * Apply a cart-bar selection to the corresponding control in the main
   * <variant-selects>, matching by `data-option-value-id`. The main
   * control's change/click event drives the rest of the theme's variant
   * update flow.
   *
   * @param {HTMLElement} cartBar - the <cart-bar> the user interacted with.
   * @param {string} optionValueId - Shopify product option value id.
   */
  function forwardSelection(cartBar, optionValueId) {
    const productInfo = cartBar.closest('product-info');
    if (!productInfo) return;

    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return;

    const target = variantSelects.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`);
    if (!target) return;

    suppressScroll(productInfo);

    if (target.tagName === 'INPUT' && target.type === 'radio') {
      if (target.disabled) return;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const popoutOption = target.matches('[data-popout-option]')
      ? target
      : target.querySelector('[data-popout-option]');
    if (popoutOption) {
      popoutOption.click();
      return;
    }

    target.click();
  }

  /**
   * Attach a delegated change listener to a <cart-bar>. Handles both the
   * native <select> and the radio-input swatch layouts. Idempotent via
   * `data-picker-bound`; safe to call again after product-info swaps.
   *
   * @param {HTMLElement} cartBar - the <cart-bar> element to bind.
   */
  function bind(cartBar) {
    if (cartBar.dataset.pickerBound === 'true') return;
    cartBar.dataset.pickerBound = 'true';

    cartBar.addEventListener('change', (event) => {
      const element = event.target;

      const select = element.closest?.('[data-cart-bar-picker-select]');
      if (select) {
        const selected = select.selectedOptions[0];
        if (!selected) return;
        const { optionValueId } = selected.dataset;
        if (optionValueId) forwardSelection(cartBar, optionValueId);
        return;
      }

      const picker = element.closest?.('[data-cart-bar-picker]');
      if (!picker) return;
      if (element.tagName === 'INPUT' && element.type === 'radio' && element.checked) {
        const { optionValueId } = element.dataset;
        if (optionValueId) forwardSelection(cartBar, optionValueId);
      }
    });
  }

  /** Bind every <cart-bar> currently in the document. */
  function init() {
    for (const cartBar of document.querySelectorAll('cart-bar')) {
      bind(cartBar);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind after product-info swaps its subtree on product change.
  document.addEventListener('theme:product-info:loaded', init);
})();
